// #3575 -- the Score Admin half of importing a scribe tablet's saved sheets file
// (the file is built by scribe-fe local/clearMachine.js buildUnsentSheetsFile,
// #3568; the server route is POST /tablet-file/import, #3571).
//
// Pure logic, no React: read + check the file in the browser, put its items in
// the ONE global order the server needs, and send them in chunks through the
// generated client, resending whatever a call did not get to.

export const TABLET_FILE_KIND = "scribe-unsent-sheets";
export const TABLET_FILE_VERSION = 1;
// score-api MAX_ITEMS_PER_REQUEST (lib/tablet-file-import.js): a request over
// this is refused, and 25 items is what fits the ~20 s budget of one Lambda call.
export const CHUNK_SIZE = 25;

const isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * Parse the picked file's text and check what the server will check first.
 * Returns { ok: true, file } or { ok: false, reason } where reason is one of
 * "invalid" | "unknownKind" | "unknownVersion" (the UI maps each to its own words).
 */
export function readTabletFile(text) {
  let file;
  try {
    file = JSON.parse(text);
  } catch {
    return { ok: false, reason: "invalid" };
  }
  if (!isObj(file)) return { ok: false, reason: "invalid" };
  if (file.kind !== TABLET_FILE_KIND) return { ok: false, reason: "unknownKind" };
  if (file.version !== TABLET_FILE_VERSION) return { ok: false, reason: "unknownVersion" };
  return { ok: true, file };
}

/**
 * The file's items in the global order: hand-added rides, then outbox ops by
 * `seq`, then drafts. `queuedRideKeys` names the hand-added rides that ALSO
 * have a queued createRide op (the server skips their manualRides entry and
 * replays the op); it goes on EVERY chunk. `items` in the file is ignored.
 */
export function planImport(file) {
  const manualRides = Array.isArray(file.manualRides) ? file.manualRides : [];
  const sheets = Array.isArray(file.sheets) ? file.sheets : [];
  const localDrafts = Array.isArray(file.localDrafts) ? file.localDrafts : [];
  // Array.prototype.sort is stable, so ops with the same (or no) seq keep file order.
  const orderedSheets = [...sheets].sort((a, b) => (Number(a?.seq) || 0) - (Number(b?.seq) || 0));
  const opTempIds = new Set(
    sheets.filter((op) => op?.type === "createRide" && typeof op.tempId === "string").map((op) => op.tempId),
  );
  const queuedRideKeys = manualRides
    .map((r) => r?.tempId ?? r?.id)
    .filter((id) => typeof id === "string" && opTempIds.has(id));
  return {
    items: [
      ...manualRides.map((value) => ({ section: "manualRides", value })),
      ...orderedSheets.map((value) => ({ section: "sheets", value })),
      ...localDrafts.map((value) => ({ section: "localDrafts", value })),
    ],
    queuedRideKeys,
  };
}

export const emptyTotals = () => ({ imported: 0, held: 0, alreadyImported: 0, inProgress: 0 });

function addSummary(totals, summary) {
  return {
    imported: totals.imported + (summary?.imported ?? 0),
    held: totals.held + (summary?.held ?? 0),
    alreadyImported: totals.alreadyImported + (summary?.alreadyImported ?? 0),
    inProgress: totals.inProgress + (summary?.inProgress ?? 0),
  };
}

/**
 * Send `pending` items in chunks of CHUNK_SIZE until none are left or a call
 * stops. `send(body)` is the generated client call (resolves to the unwrapped
 * `data`). Anything a call defers (a suffix of the chunk it was given, in
 * order) goes back to the FRONT of `pending`, so global order is kept across
 * chunks and across a Retry. Never drops an item: if the call stopped or threw,
 * every item not decided stays in `pending` for the Retry.
 *
 * Returns { totals, pending, stopped, fileShow, station, showIds, differentShow, error }.
 * `error` is set when a call threw (a refused file, a network failure); the
 * chunk's items stay in `pending`.
 */
export async function runImport({ send, file, items, queuedRideKeys, activeShowId, totals = emptyTotals(), state = {} }) {
  let pending = items;
  let acc = totals;
  const showIds = new Set(state.showIds ?? []);
  let fileShow = state.fileShow ?? null;
  let station = state.station ?? null;
  let differentShow = state.differentShow ?? false;
  let stopped = null;
  let error = null;

  while (pending.length > 0) {
    const chunk = pending.slice(0, CHUNK_SIZE);
    let data;
    try {
      data = await send({
        file,
        activeShowId: activeShowId || null,
        manualRides: chunk.filter((i) => i.section === "manualRides").map((i) => i.value),
        sheets: chunk.filter((i) => i.section === "sheets").map((i) => i.value),
        localDrafts: chunk.filter((i) => i.section === "localDrafts").map((i) => i.value),
        queuedRideKeys,
      });
    } catch (err) {
      error = err;
      break;
    }
    acc = addSummary(acc, data?.summary);
    for (const id of data?.showIds ?? []) showIds.add(id);
    fileShow = data?.fileShow ?? fileShow;
    station = data?.station ?? station;
    differentShow = differentShow || data?.differentShow === true;
    const deferred = Math.min(Math.max(Number(data?.deferred) || 0, 0), chunk.length);
    // The unprocessed suffix of this chunk goes back in front of the rest.
    pending = [...chunk.slice(chunk.length - deferred), ...pending.slice(chunk.length)];
    if (data?.stopped) {
      stopped = data.stopped;
      break;
    }
    // Deferred with no `stopped` is the server's time budget: just send them again.
    // A call that decided nothing and gave no reason would loop forever: treat as stopped.
    if (deferred === chunk.length) {
      stopped = { code: null, status: null };
      break;
    }
  }

  return {
    totals: acc,
    pending,
    stopped,
    error,
    fileShow,
    station,
    showIds: [...showIds],
    differentShow,
  };
}
